import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { admin, api } from '../../api/client.js'
import { useApi } from '../../api/useApi.js'
import { timeAgo } from '../../lib.js'
import { SOCIAL_ICONS } from '../socialIcons.js'
import { btn, Field, inputClass, Notice, Section, useAction } from './ui.jsx'

const PLATFORMS = ['youtube', 'twitch', 'kick', 'custom']
const NEEDS_URL = new Set(['kick', 'custom'])
const STATUS_TONE = {
  live: 'bg-teal text-on-teal',
  starting: 'bg-mint text-ink',
  waiting: 'bg-paper text-subtle',
  error: 'bg-live text-white',
  off: 'bg-paper text-faint',
}
function PlatformMark({ platform }) {
  const icon = SOCIAL_ICONS[platform]
  if (!icon) {
    return (
      <span className="flex size-8 items-center justify-center rounded-full bg-ink text-sm font-black text-on-ink">
        ⟳
      </span>
    )
  }
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" aria-hidden="true">
      <path d={icon.path} fill={icon.color} />
    </svg>
  )
}

/** YouTube / Twitch / Kick: where this feeder's camera is re-broadcast. */
export function Restreams({ slug }) {
  const { t } = useTranslation()
  const list = useApi(() => admin.restreams(slug), [slug])
  const config = useApi(() => api.appConfig())
  const serverOff = config.data && !config.data.features?.restream
  const { status, busy, run } = useAction()
  const [form, setForm] = useState({ platform: 'youtube', ingest_url: '', stream_key: '', watch_url: '', enabled: true })
  const [newKey, setNewKey] = useState({})

  // Status changes on the server (starting -> live): poll while this section is open.
  useEffect(() => {
    const id = setInterval(list.reload, 10000)
    return () => clearInterval(id)
  }, [list.reload])

  const add = async (e) => {
    e.preventDefault()
    const body = { ...form, ingest_url: form.ingest_url || null, watch_url: form.watch_url || null }
    if (await run(() => admin.addRestream(slug, body), t('admin.restream.added'))) {
      setForm((f) => ({ ...f, stream_key: '', ingest_url: '', watch_url: '' }))
      list.reload()
    }
  }
  const update = async (id, body, ok) => {
    if (await run(() => admin.updateRestream(id, body), ok)) list.reload()
  }
  const remove = async (id) => {
    if (!window.confirm(t('admin.restream.deleteConfirm'))) return
    await run(() => admin.deleteRestream(id), t('admin.restream.deleted'))
    list.reload()
  }

  return (
    <Section title={t('admin.sections.restreams')}>
      <p className="text-sm text-subtle">{t('admin.restream.lead')}</p>
      {serverOff ? (
        <p role="note" className="rounded-2xl bg-mint p-4 text-sm font-bold text-ink">
          {t('admin.restream.serverOff')}
        </p>
      ) : null}
      {(list.data ?? []).map((r) => (
        <div key={r.id} className="flex flex-col gap-3 rounded-2xl border border-line p-4">
          <div className="flex flex-wrap items-center gap-3">
            <PlatformMark platform={r.platform} />
            <span className="text-lg font-extrabold">{t(`admin.restream.platforms.${r.platform}`)}</span>
            <span className={`rounded-full px-3 py-1 text-xs font-extrabold ${STATUS_TONE[r.status]}`}>
              {t(`admin.restream.status.${r.status}`)}
              {r.status === 'live' && r.started_at ? ` · ${timeAgo(r.started_at)}` : ''}
            </span>
            <label className="ml-auto flex cursor-pointer items-center gap-2 text-sm font-bold">
              <input
                type="checkbox"
                role="switch"
                checked={r.enabled}
                disabled={busy}
                onChange={(e) => update(r.id, { enabled: e.target.checked })}
                className="size-5 accent-teal"
              />
              {t('admin.restream.enabled')}
            </label>
          </div>
          <div className="grid gap-1 text-sm text-muted">
            <span className="break-all">{r.ingest_url} / {r.stream_key_hint}</span>
            {r.watch_url ? (
              <a href={r.watch_url} target="_blank" rel="noopener noreferrer" className="break-all text-teal">
                {r.watch_url}
              </a>
            ) : null}
            {r.last_error ? <span className="font-bold whitespace-pre-wrap text-live">{r.last_error}</span> : null}
          </div>
          <div className="flex flex-wrap items-end gap-2">
            <input
              type="password"
              autoComplete="off"
              placeholder={t('admin.restream.newKey')}
              value={newKey[r.id] ?? ''}
              onChange={(e) => setNewKey((k) => ({ ...k, [r.id]: e.target.value }))}
              className={`${inputClass} max-w-xs`}
            />
            <button
              type="button"
              disabled={busy || !(newKey[r.id] ?? '').trim()}
              onClick={() => update(r.id, { stream_key: newKey[r.id].trim() }, t('admin.restream.keySaved')).then(() => setNewKey((k) => ({ ...k, [r.id]: '' })))}
              className={btn.ghost}
            >
              {t('admin.restream.saveKey')}
            </button>
            <button type="button" disabled={busy} onClick={() => remove(r.id)} className={`${btn.danger} ml-auto`}>
              {t('admin.restream.delete')}
            </button>
          </div>
        </div>
      ))}

      <form onSubmit={add} className="grid gap-3 rounded-2xl bg-paper p-4 md:grid-cols-2">
        <Field label={t('admin.restream.platform')}>
          <select value={form.platform} onChange={(e) => setForm({ ...form, platform: e.target.value })} className={inputClass}>
            {PLATFORMS.map((p) => (
              <option key={p} value={p}>
                {t(`admin.restream.platforms.${p}`)}
              </option>
            ))}
          </select>
        </Field>
        <Field
          label={t('admin.restream.ingestUrl')}
          hint={NEEDS_URL.has(form.platform) ? t('admin.restream.ingestRequired') : t('admin.restream.ingestDefault')}
        >
          <input
            value={form.ingest_url}
            required={NEEDS_URL.has(form.platform)}
            placeholder="rtmps://…"
            onChange={(e) => setForm({ ...form, ingest_url: e.target.value })}
            className={inputClass}
          />
        </Field>
        <Field label={t('admin.restream.streamKey')} hint={t('admin.restream.keyHint')}>
          <input
            type="password"
            required
            autoComplete="off"
            value={form.stream_key}
            onChange={(e) => setForm({ ...form, stream_key: e.target.value })}
            className={inputClass}
          />
        </Field>
        <Field label={t('admin.restream.watchUrl')} hint={t('admin.restream.watchHint')}>
          <input
            type="url"
            placeholder="https://www.youtube.com/@urbanpurr/live"
            value={form.watch_url}
            onChange={(e) => setForm({ ...form, watch_url: e.target.value })}
            className={inputClass}
          />
        </Field>
        <label className="flex items-center gap-2 text-sm font-bold">
          <input type="checkbox" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} className="size-5 accent-teal" />
          {t('admin.restream.startNow')}
        </label>
        <button type="submit" disabled={busy} className={`${btn.primary} md:justify-self-end`}>
          {t('admin.restream.add')}
        </button>
      </form>
      <p className="text-xs text-subtle">{t('admin.restream.trafficNote')}</p>
      <Notice status={status} />
    </Section>
  )
}

/** Clips of this feeder: watch, tag cats (followers get a push), delete, record now. */
export function Clips({ slug }) {
  const { t } = useTranslation()
  const clips = useApi(() => admin.clips(slug), [slug])
  const cats = useApi(() => api.cats())
  const { status, busy, run } = useAction()
  const [tags, setTags] = useState({})
  const [playing, setPlaying] = useState(null)

  const tagsOf = (c) => tags[c.id] ?? c.cats.map((x) => x.id)
  const toggleCat = (c, catId) => {
    const current = new Set(tagsOf(c))
    if (current.has(catId)) current.delete(catId)
    else current.add(catId)
    setTags((s) => ({ ...s, [c.id]: [...current] }))
  }
  const save = async (c) => {
    if (await run(() => admin.tagClip(c.id, tagsOf(c)), t('admin.clips.tagged'))) {
      setTags((s) => {
        const { [c.id]: _, ...rest } = s
        return rest
      })
      clips.reload()
    }
  }
  const remove = async (c) => {
    if (!window.confirm(t('admin.clips.deleteConfirm'))) return
    await run(() => admin.deleteClip(c.id), t('admin.clips.deleted'))
    clips.reload()
  }
  const record = async () => {
    await run(() => admin.recordClip(slug), t('admin.clips.recording'))
    setTimeout(clips.reload, 45000)
  }

  return (
    <Section
      title={t('admin.sections.clips')}
      actions={
        <button type="button" disabled={busy} onClick={record} className={btn.ghost}>
          ⏺ {t('admin.clips.recordNow')}
        </button>
      }
    >
      <p className="text-sm text-subtle">{t('admin.clips.lead')}</p>
      <Notice status={status} />
      {clips.data?.length === 0 ? <p className="text-muted">{t('admin.clips.empty')}</p> : null}
      <div className="grid grid-cols-[repeat(auto-fill,minmax(min(300px,100%),1fr))] gap-4">
        {(clips.data ?? []).map((c) => {
          const dirty = tags[c.id] !== undefined
          return (
            <div key={c.id} className="flex flex-col gap-2.5 rounded-2xl border border-line p-3">
              {playing === c.id ? (
                <video src={c.video_url} controls autoPlay muted playsInline className="aspect-video w-full rounded-xl bg-black" />
              ) : (
                <button
                  type="button"
                  onClick={() => setPlaying(c.id)}
                  className="relative flex aspect-video w-full cursor-pointer items-center justify-center overflow-hidden rounded-xl bg-slate"
                >
                  {c.poster_url ? <img src={c.poster_url} alt="" className="absolute inset-0 size-full object-cover" /> : null}
                  <span className="relative rounded-full bg-white/90 px-3 py-1.5 font-black text-[#1e2a35]">▶</span>
                </button>
              )}
              <span className="text-sm font-bold text-subtle">
                {t(`cabinet.kind.${c.kind}`)} · {timeAgo(c.ts)}
              </span>
              <div className="flex flex-wrap gap-1.5" role="group" aria-label={t('admin.clips.cats')}>
                {(cats.data ?? []).map((cat) => {
                  const on = tagsOf(c).includes(cat.id)
                  return (
                    <button
                      key={cat.id}
                      type="button"
                      aria-pressed={on}
                      onClick={() => toggleCat(c, cat.id)}
                      className={`cursor-pointer rounded-full px-2.5 py-1 text-xs font-extrabold ${on ? 'bg-teal text-on-teal' : 'border border-edge text-ink'}`}
                    >
                      {cat.name}
                    </button>
                  )
                })}
              </div>
              <div className="flex gap-2">
                <button type="button" disabled={busy || !dirty} onClick={() => save(c)} className={btn.primary}>
                  {t('admin.clips.saveTags')}
                </button>
                <button type="button" disabled={busy} onClick={() => remove(c)} className={`${btn.danger} ml-auto`}>
                  {t('admin.clips.delete')}
                </button>
              </div>
            </div>
          )
        })}
      </div>
    </Section>
  )
}
